import { ExtranetCustomerUserRoleEnum } from '@pharmac-extranet/extranet-api'
import { formatDate } from 'date-fns'
import { PropsWithChildren, useEffect, useState } from 'react'
import { useAuthenticationContext } from '../features/authentication/useAuthentication'
import { useCustomerContext } from '../features/customer/CustomerProvider'
import { mapArrayKey } from '../utils/array.utils'

export type RouteGuardProps = PropsWithChildren & {
  hasCustomer?: boolean
  allowPharmacUser?: boolean
  allowPharmacCustomerService?: boolean
  customerUserManagement?: boolean
  customerViewOrders?: boolean
  customerViewInvoices?: boolean
  doseDispensingPharmacy?: boolean
  pharmacy?: boolean
  pharmacyOrCareUnit?: boolean
}

export const CheckAuthorizationResultEnum = {
  Success: 'AC_SUCCESS',
  PharmacSuccess: 'AC_PHARMAC_SUCCESS',
  PharmacCustomerServiceSuccess: 'AC_PHARMAC_CS_SUCCESS',
  NoFlagsSuccess: 'AC_NF_SUCCESS',
  NoCustomerFailure: 'AC_C_FAILURE',
  CustomerUserManagementFailure: 'AC_C_UM_FAILURE',
  CustomerViewOrdersFailure: 'AC_C_VO_FAILURE',
  CustomerViewInvoicesFailure: 'AC_C_VI_FAILURE',
  NoDoseDispensingPharmacyFailure: 'AC_C_NDDP_FAILURE',
  NoPharmacyFailure: 'AC_C_NP_FAILURE',
  NoPharmacyOrCareUnitFailure: 'AC_C_NPOCU_FAILURE',
} as const

export type CheckAuthorizationResult =
  (typeof CheckAuthorizationResultEnum)[keyof typeof CheckAuthorizationResultEnum]

export type RouteProtectionFlags = Omit<RouteGuardProps, 'children'>

export const checkAuthorizationFlags = (
  flags: RouteProtectionFlags | undefined,
  userFlags: RouteProtectionFlags
): CheckAuthorizationResult => {
  if (!flags) return CheckAuthorizationResultEnum.NoFlagsSuccess

  if (flags.allowPharmacUser && userFlags.allowPharmacUser)
    return CheckAuthorizationResultEnum.PharmacSuccess
  if (flags.allowPharmacCustomerService && userFlags.allowPharmacCustomerService)
    return CheckAuthorizationResultEnum.PharmacCustomerServiceSuccess

  if (flags.hasCustomer && !userFlags.hasCustomer)
    return CheckAuthorizationResultEnum.NoCustomerFailure

  if (flags.customerUserManagement && !userFlags.customerUserManagement)
    return CheckAuthorizationResultEnum.CustomerUserManagementFailure
  if (flags.customerViewOrders && !userFlags.customerViewOrders)
    return CheckAuthorizationResultEnum.CustomerViewOrdersFailure

  if (flags.doseDispensingPharmacy && !userFlags.doseDispensingPharmacy)
    return CheckAuthorizationResultEnum.NoDoseDispensingPharmacyFailure
  if (flags.pharmacy && !userFlags.pharmacy) return CheckAuthorizationResultEnum.NoPharmacyFailure
  if (flags.pharmacyOrCareUnit && !userFlags.pharmacyOrCareUnit)
    return CheckAuthorizationResultEnum.NoPharmacyOrCareUnitFailure
  if (flags.customerViewInvoices && !userFlags.customerViewInvoices) {
     return CheckAuthorizationResultEnum.CustomerViewInvoicesFailure
  }

  return CheckAuthorizationResultEnum.Success
}

export const useAuthorizationFlags = (): RouteProtectionFlags & {
  isResponsiblePerson?: boolean
} => {
  const hook = useCustomerContext()
  const { isPharmacCustomerService, isPharmacUser } = useAuthenticationContext()

  const companyRole = hook.customer?.companyRole?.role

  const flags: RouteProtectionFlags = {
    hasCustomer: !!hook.customer,
    allowPharmacCustomerService: isPharmacCustomerService,
    allowPharmacUser: isPharmacUser,
    customerUserManagement: false,
    customerViewOrders: false,
    customerViewInvoices: false,
    doseDispensingPharmacy: companyRole === 'A01',
    pharmacy: companyRole === 'A01' || companyRole === 'A02',
    pharmacyOrCareUnit:
      companyRole === 'A01' ||
      companyRole === 'A02' ||
      companyRole === 'A03' ||
      companyRole === 'A04',
  }

  if (hook.roles) {
    const isAdmin = hook.roles.isAdmin
    const userRoles = mapArrayKey(hook.roles.roles, 'role') || []

    const { CustomerUserManagement, CustomerViewOrders, InvoiceViewing } = ExtranetCustomerUserRoleEnum

    flags.customerViewOrders = isAdmin || userRoles.includes(CustomerViewOrders)
    flags.customerUserManagement = isAdmin || userRoles.includes(CustomerUserManagement)
    flags.customerViewInvoices = isAdmin || userRoles.includes(InvoiceViewing)
  }

  return flags
}

export const useIsAuthorized = (
  routeFlags: RouteProtectionFlags
): CheckAuthorizationResult | true => {
  const userFlags = useAuthorizationFlags()

  const result = checkAuthorizationFlags(routeFlags, userFlags)

  return result.includes('SUCCESS') ? true : result
}

export const AuthorizedRoot = (props: RouteGuardProps) => {
  const { children, ...routeProps } = props

  const [pass, setPass] = useState(false)

  const authResult = useIsAuthorized(routeProps)

  useEffect(() => {
    setPass(authResult === true)
    if (typeof authResult !== 'boolean') {
      console.log(
        formatDate(new Date(), 'hh:mm:ss'),
        'AuthorizedRoot :: DEBUG :: authResult',
        authResult
      )
    }
  }, [authResult])

  if (!pass) return null

  return children
}
